/**
 * Utility functions for managing order-related localStorage data
 */

const ORDER_STORAGE_KEYS = {
  CUSTOMER_DETAILS: 'customerDetails',
  CART_DATA: 'cartData',
  SELECTED_PRODUCTS: 'selectedProducts'
} as const;

/**
 * Clear all order-related data from localStorage
 */
export const clearOrderData = (): void => {
  Object.values(ORDER_STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
};

/**
 * Check if there's any order-related data in localStorage
 */
export const hasOrderData = (): boolean => {
  return Object.values(ORDER_STORAGE_KEYS).some(key => 
    localStorage.getItem(key) !== null
  );
};

/**
 * Get specific order data from localStorage
 */
export const getOrderData = <T = unknown>(key: keyof typeof ORDER_STORAGE_KEYS): T | null => {
  try {
    const data = localStorage.getItem(ORDER_STORAGE_KEYS[key]);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error(`Error parsing ${key} from localStorage:`, error);
    return null;
  }
};

/**
 * Set specific order data in localStorage
 */
export const setOrderData = (key: keyof typeof ORDER_STORAGE_KEYS, data: unknown): void => {
  try {
    localStorage.setItem(ORDER_STORAGE_KEYS[key], JSON.stringify(data));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

/**
 * Get cart data from localStorage
 */
export const getCartData = (): { items?: unknown[]; vendorInfo?: unknown } | null => {
  return getOrderData('CART_DATA') as { items?: unknown[]; vendorInfo?: unknown } | null;
};

/**
 * Remove item from cart in localStorage
 */
export const removeFromCart = (itemId: string): void => {
  try {
    const cartData = getCartData();
    if (cartData && cartData.items) {
      const updatedItems = cartData.items.filter((item: { id: string }) => item.id !== itemId);
      const updatedCartData = {
        ...cartData,
        items: updatedItems
      };
      setOrderData('CART_DATA', updatedCartData);
    }
  } catch (error) {
    console.error('Error removing item from cart:', error);
  }
};

export { ORDER_STORAGE_KEYS };